import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Refresh,
  AutoAwesome,
  CheckCircle,
  Cancel,
  Error as ErrorIcon,
  Pending,
  Assessment,
  TableChart,
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import { formatLocalDate } from '../../utils/dateUtils';

interface CLDailyData {
  date: string;
  total: number;
  completed: number;
  failed: number;
  cancelled: number;
  successRate: number;
  credits: number;
}

const CreativeLookAnalytics: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [timezone, setTimezone] = useState('UTC');

  const [overview, setOverview] = useState<any>(null);
  const [dailyAnalytics, setDailyAnalytics] = useState<CLDailyData[]>([]);
  const [templateAnalytics, setTemplateAnalytics] = useState<any[]>([]);

  useEffect(() => {
    loadData();
  }, [tabValue, startDate, endDate, timezone]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (tabValue === 0) {
        const res = await apiService.getCreativeLookOverview(startDate, endDate, timezone);
        if (res.success && res.data) {
          setOverview(res.data.overview || null);
        } else {
          setError('Failed to load overview');
        }
      } else if (tabValue === 1) {
        const res = await apiService.getDailyCreativeLookAnalytics(startDate, endDate, timezone);
        if (res.success && res.data) {
          setDailyAnalytics(res.data.analytics || []);
        } else {
          setError('Failed to load daily analytics');
        }
      } else if (tabValue === 2) {
        const res = await apiService.getCreativeLookTemplateAnalytics(startDate, endDate, 50, timezone);
        if (res.success && res.data) {
          setTemplateAnalytics(res.data.templates || []);
        } else {
          setError('Failed to load template analytics');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={1}>
          <AutoAwesome color="primary" />
          <Typography variant="h4">Creative Look Analytics</Typography>
        </Box>
        <Button
          startIcon={<Refresh />}
          onClick={loadData}
          variant="outlined"
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="End Date"
                type="date"
                fullWidth
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={timezone}
                  label="Timezone"
                  onChange={(e) => setTimezone(e.target.value)}
                >
                  <MenuItem value="UTC">UTC</MenuItem>
                  <MenuItem value="Asia/Kolkata">Asia/Kolkata (IST)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs + Content — same Paper wraps both, matching SwapAnalytics layout */}
      <Paper>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Overview" icon={<Assessment />} iconPosition="start" />
          <Tab label="Daily Analytics" icon={<Assessment />} iconPosition="start" />
          <Tab label="Template Analytics" icon={<TableChart />} iconPosition="start" />
        </Tabs>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* ── Overview ─────────────────────────────────────────────── */}
            {tabValue === 0 && (
              <Box p={3}>
                {overview ? (
                  <>
                    {/* KPI Cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      {[
                        { label: 'Total Jobs',   value: overview.totalJobs,      color: '#1976d2', icon: <Assessment /> },
                        { label: 'Completed',    value: overview.completedJobs,  color: '#4caf50', icon: <CheckCircle /> },
                        { label: 'Failed',       value: overview.failedJobs,     color: '#f44336', icon: <ErrorIcon /> },
                        { label: 'Cancelled',    value: overview.cancelledJobs,  color: '#ff9800', icon: <Cancel /> },
                        { label: 'Processing',   value: overview.processingJobs, color: '#9c27b0', icon: <Pending /> },
                        { label: 'Success Rate', value: `${overview.successRate}%`, color: '#4caf50', icon: <CheckCircle /> },
                      ].map((card) => (
                        <Grid item xs={12} sm={6} md={2} key={card.label}>
                          <Card>
                            <CardContent>
                              <Typography variant="subtitle2" color="text.secondary">
                                {card.label}
                              </Typography>
                              <Typography variant="h4" sx={{ color: card.color }}>
                                {card.value}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>

                    {/* Top Templates */}
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Top Templates</Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Category</TableCell>
                                <TableCell align="right">Uses</TableCell>
                                <TableCell align="right">Completed</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(overview.topTemplates || []).length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">No template data</Typography>
                                  </TableCell>
                                </TableRow>
                              ) : (
                                (overview.topTemplates || []).map((t: any) => (
                                  <TableRow key={t.templateId || t.name} hover>
                                    <TableCell>{t.name}</TableCell>
                                    <TableCell>
                                      <Chip label={t.category} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography fontWeight="bold">{t.count}</Typography>
                                    </TableCell>
                                    <TableCell align="right">{t.completed}</TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                    No overview data available
                  </Typography>
                )}
              </Box>
            )}

            {/* ── Daily Analytics ──────────────────────────────────────── */}
            {tabValue === 1 && (
              <Box p={3}>
                {/* Daily Table */}
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Completed</TableCell>
                        <TableCell align="right">Failed</TableCell>
                        <TableCell align="right">Cancelled</TableCell>
                        <TableCell align="right">Success Rate</TableCell>
                        <TableCell align="right">Credits</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dailyAnalytics.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">
                              No analytics data found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        [...dailyAnalytics].sort((a, b) => b.date.localeCompare(a.date)).map((row) => (
                          <TableRow key={row.date} hover>
                            <TableCell>{formatLocalDate(row.date, 'MMM dd, yyyy')}</TableCell>
                            <TableCell align="right">
                              <Typography fontWeight="bold">{row.total}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Chip label={row.completed} size="small" color="success" variant="outlined" />
                            </TableCell>
                            <TableCell align="right">
                              <Chip label={row.failed} size="small" color="error" variant="outlined" />
                            </TableCell>
                            <TableCell align="right">
                              <Chip label={row.cancelled} size="small" color="warning" variant="outlined" />
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${row.successRate}%`}
                                size="small"
                                color={row.successRate >= 80 ? 'success' : row.successRate >= 50 ? 'warning' : 'error'}
                              />
                            </TableCell>
                            <TableCell align="right">{row.credits}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* ── Template Analytics ───────────────────────────────────── */}
            {tabValue === 2 && (
              <Box p={3}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Template</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Completed</TableCell>
                        <TableCell align="right">Failed</TableCell>
                        {/* <TableCell align="right">Success Rate</TableCell> */}
                        {/* <TableCell align="right">Avg Time (s)</TableCell> */}
                        {/* <TableCell align="right">Credits</TableCell> */}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {templateAnalytics.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">
                              No template data for the selected period
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        templateAnalytics.map((row) => (
                          <TableRow key={row.templateId || row.name} hover>
                            <TableCell>
                              <Typography variant="body2">{row.name}</Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={row.category} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight="bold">{row.total}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Chip label={row.completed} size="small" color="success" variant="outlined" />
                            </TableCell>
                            <TableCell align="right">
                              <Chip label={row.failed} size="small" color="error" variant="outlined" />
                            </TableCell>
                            {/* <TableCell align="right">
                              <Chip
                                label={`${row.successRate}%`}
                                size="small"
                                color={row.successRate >= 80 ? 'success' : row.successRate >= 50 ? 'warning' : 'error'}
                              />
                            </TableCell>
                            <TableCell align="right">{row.avgProcessingTime ?? 'N/A'}</TableCell>
                            <TableCell align="right">{row.totalCredits}</TableCell> */}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default CreativeLookAnalytics;
